import {computed, ref, watch} from "@vue/composition-api";
import store from "@/store";
import {formatDate} from "@core/utils/filter";
import {onFieldChange} from "@/utils/filter";

// Notification
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default function useProjectsList() {
    // Use toast
    const toast = useToast();

    const refProjectListTable = ref(null);

    // Table Handlers
    const tableColumns = [
        {key: "project_code", sortable: true},
        {key: "brief"},
        {key: "type"},
        {
            key: "won_at",
            label: "Gagné le",
            formatter: date => formatDate(date),
            sortable: true,
        },
        // { key: 'status' },
        {key: "responsible", label: "Responsable"},
        {key: "actions"},
    ];
    const stateOptions = [
        {label: "Fermé", value: "closed"},
        {label: "Ouvert", value: "opened"},
    ];
    const perPage = ref(10);
    const totalProjects = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const customerQuery = ref("");
    const locationQuery = ref("");
    const sortBy = ref("created_at");
    const suggestLimit = ref(10);
    const isSortDirDesc = ref(true);
    const countryOptions = ref([]);
    const departmentsOptions = ref([]);
    const customerOptions = ref([]);
    const stateFilter = ref(null);
    const typeFilter = ref(null);
    const customersFilter = ref([]);

    const dataMeta = computed(() => {
        const localItemsCount = refProjectListTable.value
            ? refProjectListTable.value.localItems.length
            : 0;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalProjects.value,
        };
    });

    const projectStateClass = (projectInv) => {
        if (projectInv?.set_as_lost && projectInv.set_as_lost === true) {
            return {class: "border border-1 border-danger rounded-pill text-danger", text: "Perdu"}
        } else if (projectInv?.is_closed && projectInv.is_closed === true) {
            return {class: "border border-1 border-secondary rounded-pill text-secondary", text: "Fermé"}
        }
        return {class: "", text: ""}
    };

    const refetchData = () => {
        refProjectListTable.value.refresh();
    };

    watch(
        [
            currentPage,
            perPage,
            locationQuery,
            customerQuery,
            searchQuery,
            stateFilter,
            typeFilter,
        ],
        () => {
            refetchData();
        }
    );

    watch(customerOptions, (newVal) => {
        customersFilter.value = [
            {data : newVal }
        ]
    })

    // get all project's properties to create or edit a project
    const fetchProjectProperties = (properties) => {
        let params = {
            props: properties,
        };
        store
            .dispatch("project/fetchProperties", params)
            .then(({customers, countries, departments}) => {
                customerOptions.value = customers;
                countryOptions.value = countries;
                departmentsOptions.value = departments;
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Erreur de récupération des données (clients)",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            });
    };

    const fetchProjects = (ctx, callback) => {
        store
            .dispatch("project/fetchProjects", {
                q: searchQuery.value,
                customer: customerQuery.value,
                location: locationQuery.value,
                perPage: perPage.value,
                page: currentPage.value,
                sortBy: sortBy.value,
                sortDesc: isSortDirDesc.value,
                type: typeFilter.value ?? "",
                state: stateFilter.value ?? "",
            })
            .then((response) => {
                const {projects, total} = response;

                let projectMapped = projects.map((p) => {
                    if (p.deleted_at) p._rowVariant = "danger";
                    return p;
                });

                callback(projectMapped);
                totalProjects.value = total;
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Erreur de récupération des projets",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            });
    };

    // *===============================================---*
    // *--------- UI ---------------------------------------*
    // *===============================================---*

    const resolveProjectTypeVariant = (type) => {
        if (type === "étude") return "primary";
        if (type === "contrôle") return "info";
        if (type === "étude et contrôle") return "success";
        return "primary";
    };

    const resolveProjectTypeIcon = (type) => {
        if (type === "étude") return "UserIcon";
        if (type === "contrôle") return "SettingsIcon";
        if (type === "étude et contrôle") return "ServerIcon";
        return "UserIcon";
    };

    const onInputChange = (input, resource = "customer") => {
        switch (resource) {
            case "customer":
                customersFilter.value = onFieldChange(
                    input,
                    customerOptions.value,
                    suggestLimit.value
                );
                break;
            // case 'country':
            //     countriesFilter.value = onFieldChange(input, dataCountries.value, suggestLimit.value)
            //     break;

            default:
                break;
        }
    };


    return {
        fetchProjects,
        fetchProjectProperties,
        tableColumns,
        perPage,
        currentPage,
        totalProjects,
        dataMeta,
        perPageOptions,
        searchQuery,
        customerQuery,
        locationQuery,
        sortBy,
        suggestLimit,
        onInputChange,
        isSortDirDesc,
        refProjectListTable,

        resolveProjectTypeVariant,
        resolveProjectTypeIcon,
        projectStateClass,
        refetchData,

        // Extra Filters
        stateFilter,
        typeFilter,
        customersFilter,
        customerOptions,
        stateOptions,
        countryOptions,
        departmentsOptions,
    };
}
